import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FuelCellGrid } from "./components/FuelCellGrid";
import { FuelCellSummary } from "./components/FuelCellSummary";
import { FuelCellUsageChart } from "./components/FuelCellUsageChart";
import { useFuelCellData } from "./hooks/useFuelCellData";
import { useFuelCellFromToDate } from "./hooks/useFuelCellFromToDate";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { mapLazy } from "@/utilities/mapLazy";

const FilterStackLayout = mapLazy(
  "FilterStackLayout",
  import("@/components/layout/FilterStackLayout"),
);

/**
 * Below this many kilometres no average consumption is reported by default. Over a couple of
 * kilometres the figure is dominated by whatever the tank sensor read at each end.
 */
const DEFAULT_MIN_DISTANCE_KM = 30;

export function FuelCellPage() {
  const { t } = useTranslation();
  const [minDistanceKm, setMinDistanceKm] = useState(DEFAULT_MIN_DISTANCE_KM);
  const { fromDate, toDate, setFromDate, setToDate } = useFuelCellFromToDate();
  const {
    rows,
    samplesByDevice,
    isLoading,
    isFetching,
    error,
    groupMissing,
    truncatedDeviceNames,
    summaryStats,
  } = useFuelCellData(fromDate, toDate, minDistanceKm);

  // Counted rather than left to the grey text alone: a reader who has not been told what the
  // grey means reads it as a styling quirk, and the point of it is that those tank figures
  // cannot be relied on.
  const staleCount = rows.filter(row => row.isStale).length;

  return (
    <FilterStackLayout
      minHeight={60}
      filters={
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <DateRangeSelect
            fromDate={fromDate}
            toDate={toDate}
            onChange={(from, to) => {
              if (from) setFromDate(from);
              if (to) setToDate(to);
            }}
          />
          <TextField
            type="number"
            size="small"
            label={t("fuelCell.minDistance", "Mindeststrecke für Ø-Verbrauch (km)")}
            helperText={t("fuelCell.minDistanceHint", "0 = alle Fahrzeuge auswerten")}
            value={minDistanceKm}
            onChange={event => {
              const value = Number(event.target.value);
              setMinDistanceKm(Number.isFinite(value) && value > 0 ? value : 0);
            }}
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
            sx={{ width: { xs: "100%", sm: 260 } }}
          />
        </Stack>
      }
      content={
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 1.5 }}>
          {error && (
            <Alert severity="error" sx={{ mx: 2 }}>
              {error.message}
            </Alert>
          )}
          {groupMissing && (
            <Alert severity="warning" sx={{ mx: 2 }}>
              {t("fuelCell.groupMissing", "Group '07 Fuel Cell' not found in this database")}
            </Alert>
          )}
          {truncatedDeviceNames.length > 0 && (
            <Alert severity="warning" sx={{ mx: 2 }}>
              {t(
                "fuelCell.rangeTruncated",
                "Data limit reached for some vehicles — usage and refills may be incomplete. Try a shorter time range. Affected: {{names}}",
                { names: truncatedDeviceNames.join(", ") },
              )}
            </Alert>
          )}
          {staleCount > 0 && (
            <Alert severity="warning" sx={{ mx: 2 }}>
              {t(
                "fuelCell.staleNotice",
                "{{count}} Fahrzeug(e) sind gefahren, ohne einen Füllstand zu melden. Deren Tankwerte sind ausgegraut und nicht belastbar.",
                { count: staleCount },
              )}
            </Alert>
          )}
          {rows.length > 0 && <FuelCellSummary stats={summaryStats} />}
          {rows.length > 0 && <FuelCellUsageChart rows={rows} />}
          <FuelCellGrid
            rows={rows}
            samplesByDevice={samplesByDevice}
            isLoading={isLoading}
            isFetching={isFetching}
          />
        </Box>
      }
    />
  );
}
